import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LanguageSelectorService } from 'src/@prep/shared/language-selector/service/language-selector.service';
import { GuidedWorkflowTextData, GuidedWorkflowTextDataEN, GuidedWorkflowTextDataES, GuidedWorkflowTextDataIT } from '../guided-workflow-multi-lang';

/** Translate a dotted key with `{{param}}` placeholders. */
export type WorkflowTranslate = (key: string, params?: Record<string, unknown>) => string;

/**
 * Strings of the workflow, following the app's language selector the same way
 * every page does (static per-language classes), exposed as a `t(key)`
 * function so the guide definitions read like the workspace originals.
 */
@Injectable({ providedIn: 'root' })
export class GuidedWorkflowI18nService {
  private readonly _data = new BehaviorSubject<GuidedWorkflowTextData>(GuidedWorkflowTextDataEN);
  readonly data$: Observable<GuidedWorkflowTextData> = this._data.asObservable();

  constructor(languageSelector: LanguageSelectorService) {
    languageSelector.language$.subscribe((lang) => {
      switch (lang && lang.code) {
        case 'es': this._data.next(GuidedWorkflowTextDataES); break;
        case 'it': this._data.next(GuidedWorkflowTextDataIT); break;
        default: this._data.next(GuidedWorkflowTextDataEN); break;
      }
    });
  }

  get data(): GuidedWorkflowTextData { return this._data.value; }

  /** `t('steps.card.bodyReady', { file })` → the string with placeholders filled; the key itself when unknown. */
  readonly t: WorkflowTranslate = (key, params) => {
    const raw = key.split('.').reduce<unknown>((node, part) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined), this._data.value);
    if (typeof raw !== 'string') return key;
    if (!params) return raw;
    return raw.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => (name in params ? String(params[name]) : ''));
  };
}
